// src/pages/MeetingRoom/hooks/useMeetingLifecycle.js
import { useEffect, useRef, useState } from "react";
import {
  getPracticeMeetingById,
  markMeetingJoined,
  markMeetingStarted,
  getMeetingConversation,
} from "../../../api/meetingApi";
import {
  getTrainingPlanById,
  getTrainingConversation,
} from "../../../api/trainingApi";
import { useToast } from "../../../components/Toaster/use-toast";

export default function useMeetingLifecycle({
  routeMeetingId,
  state,
  isTrainingMode = false,
  trainingPlanId,
}) {
  const { toast } = useToast();

  // 🔹 Local state
  const [meetingData, setMeetingData] = useState(null);
  const [trainingSession, setTrainingSession] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const convRef = useRef([]);
  const originalMeetingConversationRef = useRef([]);
  const safeMeetingId = Number(routeMeetingId || state?.meetingId);

  // 🔹 Fetch meeting data
  useEffect(() => {
    const loadMeeting = async () => {
      if (meetingData) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await getPracticeMeetingById(safeMeetingId);
        setMeetingData(data);
      } catch (err) {
        console.error("❌ Failed to load meeting:", err);
        setLoadError(err.message || "Failed to load meeting");
        toast({
          title: "Failed to load meeting details",
          description: err.message || "Please try again later.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (safeMeetingId) loadMeeting();
  }, [isTrainingMode, safeMeetingId, meetingData, toast]);

  // 🔹 Fetch training session (only in training mode)
  useEffect(() => {
    if (!isTrainingMode || !trainingPlanId) return;

    const fetchTrainingSession = async () => {
      try {
        setIsLoading(true);
        const plan = await getTrainingPlanById(trainingPlanId);
        setTrainingSession(plan);
      } catch (err) {
        console.error("❌ Failed to fetch training session:", err);
        toast({
          title: "Failed to load training session",
          description: err.message || "Please try again later.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrainingSession();
  }, [isTrainingMode, trainingPlanId, toast]);

  // 🔹 Fetch conversation (switches automatically based on mode)
  useEffect(() => {
    const fetchConversation = async () => {
      try {
        let convo = [];
        let originalMeetingConversation = null;

        if (isTrainingMode && trainingPlanId) {
          [convo, originalMeetingConversation] = await Promise.all([
            getTrainingConversation(trainingPlanId).catch(() => []),
            getMeetingConversation(safeMeetingId).catch(() => []),
          ]);
        } else if (!isTrainingMode && safeMeetingId) {
          convo = await getMeetingConversation(safeMeetingId);
        }

        if (Array.isArray(convo)) {
          setConversation(convo);
          convRef.current = convo;
          originalMeetingConversationRef.current = Array.isArray(originalMeetingConversation)
            ? originalMeetingConversation
            : convo;
        }
      } catch (err) {
        console.error("❌ Failed to load conversation:", err);
      }
    };

    if (isTrainingMode ? trainingPlanId : safeMeetingId) {
      fetchConversation();
    }
  }, [isTrainingMode, trainingPlanId, safeMeetingId]);

  // 🔹 Mark as joined only in practice mode
  useEffect(() => {
    if (isTrainingMode || !meetingData?.id) return;

    const markJoined = async () => {
      try {
        await markMeetingJoined(meetingData.id);
      } catch (err) {
        console.warn("⚠️ Could not mark meeting joined:", err);
      }
    };

    markJoined();
  }, [isTrainingMode, meetingData?.id]);

  return {
    meetingData,
    setMeetingData,
    trainingSession,
    conversation,
    setConversation,
    convRef,
    originalMeetingConversationRef,
    isLoading,
    loadError,
    safeMeetingId,
  };
}
